<script lang="ts" setup>
import { computed } from "vue";
import BaseChart from "@/views/shared/components/BaseChart.vue";
import { useEngineSeries, type EngineSeriesRequest } from "@/queries/engine-metrics";

/**
 * How many hosts were up, over the window. The historical host view — and a count rather than a line per
 * host because a fleet that scales does not hold still long enough for one. Staging turned over 471 distinct
 * hosts in two days and 422 of them reported inside a single hour, so a line each is hundreds of one-point
 * lines and a band each is hundreds of bands. How many were up keeps its meaning however hard the fleet
 * churns, and it is the shape of the scaling that a reader is actually after.
 *
 * <p>Outside the page's org filter, deliberately. This reads the runtime's own working-set meter, which
 * carries no org at all — narrowing it to one would match no label and draw an empty chart. A host is shared
 * infrastructure; who it was busy with is the drill under the table, not this.</p>
 */
const props = defineProps<{ minutesBack: number; stepSeconds: number }>();

const request = computed<EngineSeriesRequest>(() => ({
  // Presence, and the working set is what says it: the runtime exports one whenever the process is alive,
  // where every engine instrument only reports a host that had work.
  series: "HostMemory",
  aggregation: "Count",
  splitBy: "Instance",
  minutesBack: props.minutesBack,
  stepSeconds: props.stepSeconds,
}));

const { lines, seriesLoading } = useEngineSeries(request);

// Counting consumes its split, so there is one line and it carries no label.
const points = computed(() => lines.value[0]?.points ?? []);

const data = computed(() => ({
  labels: points.value.map((point) =>
    new Date(point.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
  ),
  datasets: [
    {
      label: "Hosts up",
      data: points.value.map((point) => point.value),
      borderColor: "#33658a",
      backgroundColor: "rgba(51, 101, 138, 0.15)",
      borderWidth: 2,
      fill: true,
    },
  ],
}));

const options = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    // A fleet count is whole hosts, so a tick of 2.5 would be a number that never happened.
    y: { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: "hosts" } },
    x: { ticks: { maxTicksLimit: 8 } },
  },
  elements: { point: { radius: 0 } },
  interaction: { mode: "index" as const, intersect: false },
}));

const peak = computed(() => (points.value.length > 0 ? Math.max(...points.value.map((p) => p.value)) : 0));
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
      <h6 class="mb-0">Hosts up</h6>
      <span v-if="peak > 0" class="badge bg-tertiary">peaked at {{ peak }}</span>
      <span v-if="seriesLoading" class="spinner-border spinner-border-sm text-muted"></span>
    </div>

    <p class="text-muted small mb-3">
      How many distinct hosts reported in each step, across the whole platform. Not one line per host — the
      fleet scales in and out far faster than a chart can name them, so how many were up is the reading that
      survives the churn.
    </p>

    <div v-if="points.length === 0 && !seriesLoading" class="text-muted small py-4 text-center">
      No host reported in this window.
    </div>

    <BaseChart v-else type="line" :data="data" :options="options" :height="160" />
  </div>
</template>
